// ChartRenderer.jsx
import React from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import momentTimezone from 'moment-timezone';

const hourlyOptions = [
  { value: 't2m', label: 'دما' },
  { value: 'tp', label: 'بارش' },
  { value: 'skt', label: 'دمای سطح خاک' },
  { value: 'fg10', label: 'سرعت باد گاستی' },
  { value: 'ws', label: 'پدیده جوی' },
];

const synopticExtraOptions = [
  { value: 'ff_10m_dmax', label: 'بیشینه سرعت باد' },
  { value: 'dd_10m_dmax', label: 'جهت باد بیشینه' },
  { value: 'ff_10m_dm', label: 'میانگین سرعت باد' },
  { value: 'd2m_dm', label: 'میانگین روزانه دمای نقطه شبنم' },
  { value: 'skt_dmin', label: 'کمینه روزانه دمای خاک' },
  { value: 'mx2t', label: 'بیشینه روزانه دما' },
  { value: 'mn2t', label: 'کمینه روزانه دما' },
  { value: 't2m_dm', label: 'میانگین روزانه دما' },
  { value: 'tp_dsum', label: 'بارش روزانه' },
  { value: 'rh2m_dm', label: 'میانگین روزانه رطوبت' },
  { value: 'rh2m_dmax', label: 'بیشینه روزانه رطوبت' },
  { value: 'rh2m_dmin', label: 'کمینه روزانه رطوبت' },
  { value: 'sshn_dsum', label: 'ساعت آفتابی' },
  { value: 'e_dsum', label: 'تبخیر و تعرق روزانه' },
  { value: 'ss_dsum', label: 'ارتفاع برف روزانه' },
];

const dailyOptions = [
  { value: 't2m_dmax', label: 'بیشینه روزانه دما' },
  { value: 'rh2m_dmax', label: 'بیشینه روزانه رطوبت' },
  { value: 't2m_dmin', label: 'کمینه روزانه دما' },
  { value: 'rh2m_dmin', label: 'کمینه روزانه رطوبت' },
  { value: 'tp_dsum', label: 'بارش روزانه' },
  { value: 'ssrd_dsum', label: 'تشعشع روزانه' },
  { value: 'sshn_dsum', label: 'ساعت آفتابی' },
  { value: 'e_dsum', label: 'تبخیر و تعرق روزانه' },
  { value: 'sde-dsum', label: 'ارتفاع برف روزانه' },
  { value: 'cu_dacc', label: 'نیاز سرمایی' },
  { value: 'rh2m_dm', label: 'میانگین روزانه رطوبت' },
  { value: 't2m_dm', label: 'میانگین روزانه دما' },
];

const allOptions = [...hourlyOptions, ...dailyOptions, ...synopticExtraOptions];


const ChartRenderer = ({
  chartData,
  selectedOptions,
  sunrise,
  sunset,
  persianDates,
  persianDatesHourly,
  hourlyData,
  translatedWsData,
  ws
}) => {
  const chartOptions = (title, data, yAxisTitle) => {
    const minValue = Math.min(...data);
    const maxValue = Math.max(...data);
    const avgValue = (minValue + maxValue) / 2;

    return {
      chart: {
        backgroundColor: '#fdfdfd',
        style: { fontFamily: "'Vazirmatn', sans-serif" },
        events: {
          load: function () {
            const chart = this;
            const labelHtml = createLabelHtml(maxValue, minValue, avgValue, 'rtl');
            addLabel(chart, labelHtml, chart.plotWidth - 160);
          },
          redraw: function () {
            const chart = this;
            if (chart.customLabel) chart.customLabel.destroy();
            const labelHtml = createLabelHtml(maxValue, minValue, avgValue, 'ltr');
            addLabel(chart, labelHtml, chart.plotWidth - 50);
          },
        },
      },
      title: {
        text: title,
        style: { fontSize: '18px', color: '#333', direction: 'rtl', fontWeight: 'bold' },
      },
      xAxis: {
        categories: persianDates,
        tickInterval: 1,
        gridLineWidth: 1,
        gridLineColor: '#eee',
        labels: { style: { color: '#666', fontSize: '12px' } },
        lineColor: '#ccc',
        tickColor: '#ccc'
      },
      yAxis: {
        title: {
          text: yAxisTitle,
          style: { fontSize: '14px', color: '#666' },
        },
        gridLineColor: '#e6e6e6',
        gridLineDashStyle: 'Dash',
        labels: { style: { fontSize: '12px', color: '#444' } },
      },
      series: [
        {
          name: '',
          data,
          type: 'areaspline',
          color: {
            linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
            stops: [[0, '#008FFB'], [1, '#00E5FF']]
          },
          fillOpacity: 0.2,
          lineWidth: 3,
          marker: {
            enabled: true,
            radius: 6,
            lineWidth: 2,
            lineColor: '#fff',
            fillColor: '#008FFB',
            states: {
              hover: {
                enabled: true,
                fillColor: '#00E5FF',
                radiusPlus: 2
              }
            }
          }
        }
      ],
      tooltip: {
        useHTML: true,
        backgroundColor: '#fff',
        borderColor: '#008FFB',
        borderRadius: 8,
        borderWidth: 2,
        shadow: true,
        style: { padding: '12px', color: '#333', fontSize: '13px', fontWeight: '500' },
        formatter: function () {
          return `<div style="padding:4px 8px;"><b>${title}:</b> ${this.y}</div>`;
        }
      },
      legend: { enabled: false },
      credits: { enabled: false },
    };
  };

  const chartOptionsHourly = (title, data, yAxisTitle, isWs = false, key) => {
    const minValue = Math.min(...data);
    const maxValue = Math.max(...data);
    const avgValue = (minValue + maxValue) / 2;

    return {
      chart: {
        backgroundColor: '#fdfdfd',
        style: { fontFamily: "'Vazirmatn', sans-serif" },
        events: {
          load: function () {
            const chart = this;
            if (key !== 'ws') {
              const labelHtml = createLabelHtml(maxValue, minValue, avgValue, 'rtl');
              addLabel(chart, labelHtml, chart.plotWidth - 160);
            }
          },
          redraw: function () {
            const chart = this;
            if (chart.customLabel) chart.customLabel.destroy();
            if (key !== 'ws') {
              const labelHtml = createLabelHtml(maxValue, minValue, avgValue, 'ltr');
              addLabel(chart, labelHtml, chart.plotWidth - 50);
            }
          },
        },
      },
      title: {
        text: title,
        style: { fontSize: '18px', color: '#333', direction: 'rtl', fontWeight: 'bold' },
      },
      xAxis: {
        categories: persianDatesHourly,
        tickInterval: 1,
        labels: {
          rotation: -60,
          style: { fontSize: '10px', color: '#666' },
        },
      },
      yAxis: {
        title: {
          text: yAxisTitle,
          style: { fontSize: '14px', color: '#666' },
        },
        gridLineColor: '#e6e6e6',
        gridLineDashStyle: 'Dash',
        labels: { style: { fontSize: '12px', color: '#444' } },
      },
      series: [
        {
          name: key,
          data: isWs ? Array(data.length).fill(2) : data,
          type: 'areaspline',
          color: {
            linearGradient: { x1: 0, y1: 0, x2: 1, y2: 0 },
            stops: [[0, '#FF5733'], [1, '#C70039']]
          },
          fillOpacity: 0.2,
          marker: {
            enabled: true,
            radius: 4,
            lineWidth: 2,
            lineColor: '#fff',
            fillColor: '#FF5733',
          },
        }
      ],
      tooltip: {
        useHTML: true,
        formatter: function () {
          const index = this.point.index;
          const dateTime = persianDatesHourly[index] || 'N/A';
          let content = `<div style="text-align: center; direction: rtl;"><div><b>${dateTime}</b></div>`;

          if (key === "ws") {
            const iconKey = ws?.[index] || 'na';
            const iconPath = `/images/c${iconKey}.svg`;
            const phenomenon = translatedWsData[index] || 'نامشخص';
            content += `
              <img src="${iconPath}" alt="${phenomenon}" style="width: 32px; height: 32px; margin-bottom: 6px;" />
              <div><b>پدیده جوی:</b> ${phenomenon}</div>`;
          } else {
            content += `<div><b>${title}:</b> ${this.y}</div>`;
          }

          content += `</div>`;
          return content;
        }
      },
      plotOptions: {
        series: {
          animation: { duration: 1000, easing: 'easeOutBounce' },
          marker: {
            states: {
              hover: { fillColor: '#FF728b', radius: 6 }
            }
          }
        }
      },
      legend: { enabled: false },
      credits: { enabled: false },
    };
  };

  const createLabelHtml = (max, min, avg, dir) => `
  <div style="
    background: #f5f5f5;
    border-radius: 8px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 8px 12px;
    box-shadow: 0 2px 6px rgba(0,0,0,0.1);
    text-align: right;
    font-size: 13px;
    line-height: 1.7;
    z-index: 10000000;
    direction: ${dir};
  ">
    <div><b>بیشینه:</b> ${max.toFixed(1)}</div>
    <div style="margin: 0 10px;"><b>کمینه:</b> ${min.toFixed(1)}</div>
    <div><b>میانگین:</b> ${avg.toFixed(1)}</div>
  </div>
`;


  const addLabel = (chart, html, x) => {
    const label = chart.renderer
      .label(html, x, chart.plotTop - 50, null, null, null, true)
      .attr({ zIndex: 10 })
      .css({ color: '#333' })
      .add();
    const labelBBox = label.getBBox();
    if (x + labelBBox.width > chart.chartWidth) {
      label.attr({ x: chart.chartWidth - labelBBox.width - 10 });
    }

    chart.customLabel = label;
  };


  return (
    <>
      {chartData.map(({ key, title, yAxis, data, isHourly }) => {
        if (!selectedOptions.includes(key) || data.length === 0) return null;

        const matched = allOptions.find((item) => item.value === key);
        const titleWithKey = matched ? `${matched.label} (${matched.value})` : `${title} (${key})`;

        return (
          <HighchartsReact
            key={key}
            highcharts={Highcharts}
            options={
              key === 'ws'
                ? chartOptionsHourly(titleWithKey, data, yAxis, true, key)
                : isHourly
                  ? chartOptionsHourly(titleWithKey, data, yAxis, false, key)
                  : chartOptions(titleWithKey, data, yAxis)
            }
          />
        );
      })}

    </>
  );
};

export default ChartRenderer;
